import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Shield, 
  Zap, 
  ChevronRight,
  Check,
  ArrowLeft,
  AlertTriangle,
  Target,
  Activity,
  Calendar,
  BarChart2,
  Percent,
  TrendingDown,
  Award,
  Download,
  FileText,
  ExternalLink,
  CheckCircle,
  Database,
  Server,
  Trophy,
  Clock,
  DollarSign,
  Users,
  Settings,
  Sliders
} from 'lucide-react';
import { ParticlesBackground } from '../components/ParticlesBackground';
import { SubscribeModal } from '../components/SubscribeModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function EAPage() {
  const [yearlyStats, setYearlyStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyData2026, setMonthlyData2026] = useState([]);
  const [transparencyInfo, setTransparencyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [yearlyRes, monthlyRes, monthly2026Res, transparencyRes] = await Promise.all([
          fetch(`${API_URL}/api/performance/2025`),
          fetch(`${API_URL}/api/performance/2025/monthly`),
          fetch(`${API_URL}/api/performance/2026/monthly`),
          fetch(`${API_URL}/api/transparency`)
        ]);
        
        const yearly = await yearlyRes.json();
        const monthly = await monthlyRes.json();
        const monthly2026 = await monthly2026Res.json();
        const transparency = await transparencyRes.json();
        
        setYearlyStats(yearly);
        setMonthlyData(monthly);
        setMonthlyData2026(monthly2026);
        setTransparencyInfo(transparency);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleSubscribe = () => {
    setShowSubscribeModal(true);
  };

  const handleDownloadReport = (month) => {
    window.open(`${API_URL}/api/reports/${month.toLowerCase()}`, '_blank');
  };

  const formatReturn = (value) => {
    if (value > 0) return `+${value}%`;
    return `${value}%`;
  };

  const getReturnColor = (value) => {
    if (value > 0) return 'text-[#10B981]';
    if (value < 0) return 'text-red-400';
    return 'text-white/60';
  };

  const strategySteps = [
    { 
      step: '01', 
      title: 'Market Structure Analysis', 
      icon: 'BarChart2',
      description: 'At the start of each session, the EA automatically maps out key price levels where the market is statistically most likely to make a significant move. These levels are recalculated fresh every single day across any instrument — no static zones, no manual intervention.'
    },
    { 
      step: '02', 
      title: 'Automated Order Placement', 
      icon: 'Target',
      description: 'Based on the identified levels, the EA places pending orders on both sides of the market simultaneously and waits. It never chases price. Entries trigger only when the market commits to a clear directional move — removing all emotion from the entry decision.'
    },
    { 
      step: '03', 
      title: 'Dynamic Trade Management', 
      icon: 'TrendingUp',
      description: 'Once in a trade, the EA actively manages the position using a structured trailing approach that locks in profits as the market moves in favour. Winning trades are never cut short — the EA lets momentum run while protecting gains at every step.'
    },
    { 
      step: '04', 
      title: 'Configurable Risk Control', 
      icon: 'Shield',
      description: 'You define your risk per trade — the EA handles the rest. Position size adjusts automatically based on your chosen risk percentage, account size, and instrument. Risk is controlled by design, not by hope.'
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-body text-white/50 text-sm">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 overflow-hidden premium-bg" data-testid="ea-header-section">
        <div className="gradient-orb gradient-orb-1" />
        <div className="gradient-orb gradient-orb-2" />
        <div className="mesh-overlay" />
        <div className="candle-pattern" />
        <div className="absolute inset-0 z-[1]"><ParticlesBackground /></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-4 transition-colors text-sm" data-testid="ea-back-link">
            <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Text Content */}
            <motion.div initial="initial" animate="animate" variants={fadeUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-3">
                <TrendingUp size={12} className="text-[#007AFF]" />
                <span className="font-mono text-xs text-white/70">MT5 Expert Advisor</span>
              </div>
              
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white hero-title mb-3" data-testid="ea-page-title">
                Monk Breakout{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#007AFF] to-[#8B5CF6]">EA</span>
              </h1>
              
              <p className="font-body text-base text-white/55 max-w-xl mb-4">
                Automated breakout trading with verified 2025 performance. Fixed 2% risk per trade. Rule-based entries and smart risk management.
              </p>
              
              <div className="flex flex-wrap items-center gap-3 mb-6 lg:mb-0">
                <button onClick={handleSubscribe} className="btn-purple flex items-center gap-2" data-testid="ea-hero-subscribe-btn">
                  <Zap size={16} /> Subscribe - ₹10,000/month
                  <ExternalLink size={12} className="opacity-60" />
                </button>
                <a href="#performance" className="btn-secondary flex items-center gap-2">
                  <BarChart2 size={16} /> See Performance
                </a>
              </div>
            </motion.div>

            {/* Right Column - MT5 Screenshot */}
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }} 
              animate={{ opacity: 1, y: 0, scale: 1 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative w-full max-w-full"
            >
              {/* Outer Glow Effect */}
              <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-r from-[#007AFF]/30 via-[#8B5CF6]/30 to-[#10B981]/30 rounded-2xl sm:rounded-3xl blur-2xl sm:blur-3xl opacity-70" />
              
              {/* Gradient Border Wrapper */}
              <div className="relative p-[2px] rounded-lg sm:rounded-xl bg-gradient-to-r from-[#007AFF] via-[#8B5CF6] to-[#10B981] w-full">
                {/* Browser Frame */}
                <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-2xl w-full" style={{background: 'linear-gradient(180deg, rgba(30,30,40,0.98) 0%, rgba(15,15,20,1) 100%)'}}>
                  {/* Browser Header */}
                  <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-3 border-b border-white/10 bg-[#1a1a24]">
                    <div className="flex gap-1 sm:gap-1.5">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#ff5f57]" />
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#febc2e]" />
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#28c840]" />
                    </div>
                    <div className="flex-1 mx-2 sm:mx-4 min-w-0">
                      <div className="bg-white/5 rounded-md px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[11px] text-white/40 font-mono truncate">
                        MetaTrader 5 - Monk_BreakoutEA
                      </div>
                    </div>
                  </div>
                  
                  {/* Screenshot with Floating Stats */}
                  <div className="relative w-full overflow-visible">
                    <img 
                      src="/mt5-screenshot.png" 
                      alt="Monk Breakout EA - MT5 Trading" 
                      className="w-full h-auto object-contain max-w-full"
                    />
                    
                    {/* Floating Return Card - Top Right */}
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.6 }}
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10"
                    >
                      <div className="glass-strong rounded-lg sm:rounded-xl p-2 sm:p-3 border border-[#10B981]/40 shadow-xl backdrop-blur-md">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-[#10B981]/25 flex items-center justify-center">
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981]" />
                          </div>
                          <div>
                            <div className="text-[8px] sm:text-[10px] text-white/60 font-body">2025 Return</div>
                            <div className="text-sm sm:text-lg font-heading font-bold text-[#10B981]">+228.66%</div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Floating Win Rate Card - Bottom Left */}
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.8 }}
                      className="absolute bottom-4 left-2 sm:bottom-6 sm:left-4 z-10"
                    >
                      <div className="glass-strong rounded-lg sm:rounded-xl p-2 sm:p-3 border border-[#007AFF]/40 shadow-xl backdrop-blur-md">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-[#007AFF]/25 flex items-center justify-center">
                            <Target className="w-3 h-3 sm:w-4 sm:h-4 text-[#007AFF]" />
                          </div>
                          <div>
                            <div className="text-[8px] sm:text-[10px] text-white/60 font-body">Win Rate</div>
                            <div className="text-sm sm:text-lg font-heading font-bold text-[#007AFF]">78.08%</div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Subtle Gradient Overlay at Bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 bg-gradient-to-t from-[#0f0f14] to-transparent pointer-events-none" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2025 Performance Overview */}
      <section className="section-compact" id="performance" data-testid="ea-performance-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6">
            <span className="font-mono text-[#10B981] text-xs uppercase tracking-wider mb-2 block">Verified Results</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">2025 Performance Overview</h2>
            <p className="font-body text-white/45 text-sm">2025 performance verified on XAUUSD M5. Strategy logic applies across liquid instruments.</p>
          </motion.div>

          {/* Compounded Annual Stats - Main Headline */}
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="performance-card p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#10B981]" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white text-sm">2025 Annual Return (Compounded)</h3>
                  <p className="font-body text-[10px] text-white/40">Full-year backtest · 2% of growing balance</p>
                </div>
              </div>
              <div className="px-2 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/20">
                <span className="font-mono text-[9px] text-[#10B981]">2025 Backtest</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/20">
                <div className="font-heading font-bold text-2xl md:text-3xl text-[#10B981]">+228.66%</div>
                <div className="font-body text-[10px] text-white/50">2025 Return</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/3">
                <div className="font-heading font-bold text-xl md:text-2xl text-[#007AFF]">78.08%</div>
                <div className="font-body text-[10px] text-white/40">Win Rate</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/3">
                <div className="font-heading font-bold text-xl md:text-2xl text-[#8B5CF6]">2.47</div>
                <div className="font-body text-[10px] text-white/40">Profit Factor</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/3">
                <div className="font-heading font-bold text-xl md:text-2xl text-yellow-500">11.12%</div>
                <div className="font-body text-[10px] text-white/40">Max Drawdown</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/3">
                <div className="font-heading font-bold text-xl md:text-2xl text-white">219</div>
                <div className="font-body text-[10px] text-white/40">Total Trades</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/3">
                <div className="font-heading font-bold text-xl md:text-2xl text-white">26.46</div>
                <div className="font-body text-[10px] text-white/40">Sharpe Ratio</div>
              </div>
            </div>

            {/* Additional Stats Row */}
            <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-3 md:grid-cols-6 gap-2">
              <div className="text-center">
                <div className="font-mono text-sm text-[#10B981]">$22,866</div>
                <div className="font-body text-[9px] text-white/30">Net Profit</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-sm text-white/70">171W / 48L</div>
                <div className="font-body text-[9px] text-white/30">Win/Loss</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-sm text-white/70">$224.60</div>
                <div className="font-body text-[9px] text-white/30">Avg Win</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-sm text-white/70">$323.75</div>
                <div className="font-body text-[9px] text-white/30">Avg Loss</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-sm text-[#10B981]">24</div>
                <div className="font-body text-[9px] text-white/30">Max Cons. Wins</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-sm text-[#EF4444]">4</div>
                <div className="font-body text-[9px] text-white/30">Max Cons. Losses</div>
              </div>
            </div>
          </motion.div>

          {/* Monthly Combined Reference */}
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="glass rounded-xl p-4 border border-white/5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center">
                  <BarChart2 className="w-4 h-4 text-[#8B5CF6]" />
                </div>
                <div>
                  <h4 className="font-heading font-medium text-white text-sm">Monthly Average: +10.70%/month</h4>
                  <p className="font-body text-[10px] text-white/40">Sum of 12 independent $10K tests = +128.37% combined</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-center">
                <div>
                  <div className="font-mono text-sm text-[#8B5CF6]">213</div>
                  <div className="font-body text-[9px] text-white/30">Monthly Trades</div>
                </div>
                <div>
                  <div className="font-mono text-sm text-white/70">77.93%</div>
                  <div className="font-body text-[9px] text-white/30">Avg Win Rate</div>
                </div>
                <div>
                  <div className="font-mono text-sm text-[#10B981]">11/12</div>
                  <div className="font-body text-[9px] text-white/30">Profitable Months</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Monthly Performance Grid */}
      <section className="section-compact section-glow" data-testid="ea-monthly-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6">
            <span className="font-mono text-[#8B5CF6] text-xs uppercase tracking-wider mb-2 block">Monthly Breakdown</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">12-Month Performance Grid</h2>
            <p className="font-body text-white/45 text-sm">Detailed monthly performance overview. Yearly report available for download.</p>
          </motion.div>

          {/* Monthly Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {monthlyData.map((month, index) => (
              <motion.div
                key={month.month}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="performance-card p-3 sm:p-4 card-hover overflow-hidden"
                data-testid={`monthly-card-${month.month.toLowerCase()}`}
              >
                {/* Header - stacked on mobile */}
                <div className="mb-2 sm:mb-3">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-[#8B5CF6] flex-shrink-0" />
                    <span className="font-heading font-semibold text-white text-xs sm:text-sm truncate">{month.month}</span>
                  </div>
                  <div className={`font-mono font-bold text-base sm:text-lg ${getReturnColor(month.return_pct)}`}>
                    {formatReturn(month.return_pct)}
                  </div>
                </div>
                
                <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
                  <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                    <span className="text-white/40 truncate">Win Rate</span>
                    <span className="font-mono text-[#007AFF] flex-shrink-0">{month.win_rate_pct?.toFixed(1) || month.win_rate_pct}%</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                    <span className="text-white/40 truncate">Profit</span>
                    <span className={`font-mono flex-shrink-0 ${month.profit_dollars >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      ${month.profit_dollars >= 0 ? '+' : ''}{month.profit_dollars?.toFixed(0) || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                    <span className="text-white/40 truncate">Trades</span>
                    <span className="font-mono text-white/60 flex-shrink-0">{month.total_trades}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                    <span className="text-white/40 truncate">W/L</span>
                    <span className="font-mono text-white/60 flex-shrink-0">{month.winning_trades || 0}W / {month.losing_trades || 0}L</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 2026 Q1 Performance Grid */}
      {monthlyData2026.length > 0 && (
        <section className="section-compact" data-testid="ea-monthly-section-2026">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6 sm:mb-8">
              <span className="font-mono text-[#10B981] text-xs uppercase tracking-wider mb-2 block">2026 Performance</span>
              <h2 className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-white mb-2">2026 Results</h2>
              <p className="font-body text-white/45 text-xs sm:text-sm">Live trading performance for January - April 2026</p>
            </motion.div>

            {/* 2026 Stats Summary */}
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-mono font-bold text-xl sm:text-2xl text-[#10B981]">
                    +{monthlyData2026.reduce((sum, m) => sum + m.return_pct, 0).toFixed(2)}%
                  </div>
                  <div className="font-body text-[10px] sm:text-xs text-white/40">Q1 Total Return</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-mono font-bold text-xl sm:text-2xl text-[#007AFF]">
                    {(monthlyData2026.reduce((sum, m) => sum + m.win_rate_pct, 0) / monthlyData2026.length).toFixed(1)}%
                  </div>
                  <div className="font-body text-[10px] sm:text-xs text-white/40">Avg Win Rate</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-mono font-bold text-xl sm:text-2xl text-white">
                    {monthlyData2026.reduce((sum, m) => sum + m.total_trades, 0)}
                  </div>
                  <div className="font-body text-[10px] sm:text-xs text-white/40">Total Trades</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <div className="font-mono font-bold text-xl sm:text-2xl text-[#10B981]">
                    +${monthlyData2026.reduce((sum, m) => sum + m.profit_dollars, 0).toFixed(0)}
                  </div>
                  <div className="font-body text-[10px] sm:text-xs text-white/40">Total Profit</div>
                </div>
              </div>
            </motion.div>

            {/* Monthly Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              {monthlyData2026.map((month, index) => (
                <motion.div
                  key={month.month}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="glass rounded-xl p-3 sm:p-4 card-hover border border-[#10B981]/20"
                >
                  <div className="mb-2 sm:mb-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981] flex-shrink-0" />
                      <span className="font-heading font-semibold text-white text-xs sm:text-sm truncate">{month.month}</span>
                      <span className="text-[10px] text-white/30">2026</span>
                    </div>
                    <div className={`font-mono font-bold text-base sm:text-lg ${getReturnColor(month.return_pct)}`}>
                      {formatReturn(month.return_pct)}
                    </div>
                  </div>
                  
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                      <span className="text-white/40 truncate">Win Rate</span>
                      <span className="font-mono text-[#007AFF] flex-shrink-0">{month.win_rate_pct}%</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                      <span className="text-white/40 truncate">Profit</span>
                      <span className="font-mono text-[#10B981] flex-shrink-0">+${month.profit_dollars?.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                      <span className="text-white/40 truncate">Trades</span>
                      <span className="font-mono text-white/60 flex-shrink-0">{month.total_trades}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] sm:text-xs gap-1">
                      <span className="text-white/40 truncate">W/L</span>
                      <span className="font-mono text-white/60 flex-shrink-0">{month.winning_trades}W / {month.losing_trades}L</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* 2026 Report Download */}
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-6 text-center">
              <button 
                onClick={() => handleDownloadReport('2026_q1')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 rounded-xl text-[#10B981] text-sm font-medium transition-colors"
                data-testid="download-2026-q1-report-btn"
              >
                <Download size={16} /> Download 2026 Report (PDF)
              </button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Full-Year Report Download */}
      <section className="section-compact" data-testid="ea-yearly-report-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="performance-card p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)'}} />
            <div className="relative z-10">
              <Award className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl md:text-2xl text-white mb-2">Full 2025 Backtest Report</h2>
              <p className="font-body text-white/50 text-sm mb-5 max-w-md mx-auto">
                Complete trade history, equity curve analysis, and detailed statistical breakdown for the entire year.
              </p>
              <button 
                onClick={() => handleDownloadReport('yearly')}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="download-yearly-report-btn"
              >
                <Download size={16} /> Download Full Year Report (PDF)
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transparency / Source Verification Section */}
      <section className="section-compact section-depth" data-testid="ea-transparency-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="font-mono text-[#007AFF] text-xs uppercase tracking-wider mb-2 block">Verification</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">Source Transparency</h2>
            <p className="font-body text-white/45 text-sm">Complete verification of backtest methodology and data sources</p>
          </motion.div>

          {transparencyInfo && (
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="performance-card p-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                    <Database className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Data Source</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.data_source}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center flex-shrink-0">
                    <Server className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Platform</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.platform}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Modeling Quality</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.modeling_quality}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Spread Assumptions</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.spread_assumptions}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Slippage Assumptions</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.slippage_assumptions}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-white/3">
                  <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                    <BarChart2 className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-white/40 mb-1">Broker Settings</div>
                    <div className="font-heading font-semibold text-white text-sm">{transparencyInfo.broker_settings}</div>
                  </div>
                </div>
              </div>

              {/* Risk Configuration Highlight */}
              <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-[#10B981]/10 to-[#007AFF]/10 border border-[#10B981]/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-[#10B981] mb-0.5">Risk Configuration</div>
                    <div className="font-heading font-bold text-white text-lg">{transparencyInfo.risk_configuration}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Strategy Section - How It Works */}
      <section className="section-compact" data-testid="ea-strategy-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="font-mono text-[#8B5CF6] text-xs uppercase tracking-wider mb-2 block">Strategy</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">How It Works</h2>
            <p className="font-body text-white/45 text-sm max-w-2xl mx-auto">
              A systematic, rule-based approach to any liquid market. No discretion. No emotion. Just structure.
            </p>
          </motion.div>

          {/* Numbered Steps Flow */}
          <div className="relative">
            {/* Connecting Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-[2px] bg-gradient-to-r from-[#8B5CF6]/50 via-[#007AFF]/50 to-[#10B981]/50" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
              {strategySteps.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Step Number Circle */}
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/15 border-2 border-[#8B5CF6]/40 flex items-center justify-center relative z-10">
                      <span className="font-heading font-bold text-lg text-[#8B5CF6]">{item.step}</span>
                    </div>
                    {/* Mobile connecting line */}
                    {index < strategySteps.length - 1 && (
                      <div className="lg:hidden w-[2px] h-6 bg-gradient-to-b from-[#8B5CF6]/40 to-transparent mt-2" />
                    )}
                  </div>

                  {/* Content Card */}
                  <div className="glass rounded-xl p-5 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        index === 0 ? 'bg-[#8B5CF6]/15' :
                        index === 1 ? 'bg-[#007AFF]/15' :
                        index === 2 ? 'bg-[#10B981]/15' :
                        'bg-[#F59E0B]/15'
                      }`}>
                        {index === 0 && <BarChart2 className="w-5 h-5 text-[#8B5CF6]" />}
                        {index === 1 && <Target className="w-5 h-5 text-[#007AFF]" />}
                        {index === 2 && <TrendingUp className="w-5 h-5 text-[#10B981]" />}
                        {index === 3 && <Shield className="w-5 h-5 text-[#F59E0B]" />}
                      </div>
                      <h3 className="font-heading font-semibold text-white text-sm">{item.title}</h3>
                    </div>
                    <p className="font-body text-xs text-white/50 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Proprietary Callout Box */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-10 max-w-4xl mx-auto"
          >
            <div className="relative p-6 rounded-2xl bg-[#0c0c14] border border-white/10 overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/5 via-transparent to-[#007AFF]/5" />
              
              <div className="relative flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#8B5CF6]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-heading font-semibold text-white text-sm mb-2">Proprietary Strategy</h4>
                  <p className="font-body text-xs text-white/50 leading-relaxed">
                    The exact level calculation, session filters, and trailing logic are proprietary to Monk Algo and are not publicly disclosed. The strategy has been independently backtested on XAUUSD M5 across the full year 2025 using 100% tick quality data from Dukascopy Bank SA. Results on other instruments may vary.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Risk Model Explanation Section */}
      <section className="section-compact section-glow" data-testid="ea-risk-model-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="font-mono text-[#10B981] text-xs uppercase tracking-wider mb-2 block">Trading Philosophy</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">Smart Risk Management</h2>
            <p className="font-body text-white/45 text-sm">Configurable risk controls for disciplined, consistent trading</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0 }} className="glass rounded-xl p-6 card-hover">
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/15 flex items-center justify-center mb-4">
                <Percent className="w-6 h-6 text-[#10B981]" />
              </div>
              <h3 className="font-heading font-semibold text-white mb-2">Your Risk, Your Rules</h3>
              <p className="font-body text-xs text-white/50 leading-relaxed">
                Set your preferred risk percentage per trade. The EA automatically calculates position size based on your account equity and stop-loss distance.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="glass rounded-xl p-6 card-hover">
              <div className="w-12 h-12 rounded-xl bg-[#007AFF]/15 flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-[#007AFF]" />
              </div>
              <h3 className="font-heading font-semibold text-white mb-2">Rule-Based Execution</h3>
              <p className="font-body text-xs text-white/50 leading-relaxed">
                No discretionary trading. The EA follows predefined rules for entry, stop-loss, and take-profit. Zero emotional decisions.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="glass rounded-xl p-6 card-hover">
              <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#8B5CF6]" />
              </div>
              <h3 className="font-heading font-semibold text-white mb-2">Drawdown Control</h3>
              <p className="font-body text-xs text-white/50 leading-relaxed">
                Built-in maximum drawdown limits protect capital during losing streaks. Automatic exposure reduction if thresholds are exceeded.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prop Firm Compatible Section */}
      <section className="section-compact" data-testid="ea-prop-firm-section">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 mb-4">
              <Trophy size={12} className="text-[#F59E0B]" />
              <span className="font-mono text-xs text-[#F59E0B]">Prop Firm Compatible</span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">
              Pass Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#EF4444]">
                Prop Firm Challenge
              </span>
            </h2>
            <p className="font-body text-white/45 text-sm max-w-2xl mx-auto">
              Works with prop firms that allow MT5 Expert Advisors. Fully customizable risk settings to match any challenge rules.
            </p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left - EA Features for Prop Firms */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Customizable Risk Card */}
              <div className="glass-strong rounded-xl p-5 border border-[#007AFF]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                    <Sliders className="w-6 h-6 text-[#007AFF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-white mb-1">Customizable Risk Settings</h3>
                    <p className="font-body text-xs text-white/50 mb-3">Configure risk to match your prop firm's rules</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2.5 py-1 rounded-md bg-[#007AFF]/10 border border-[#007AFF]/20 text-[10px] font-mono text-[#007AFF]">Risk %</span>
                      <span className="px-2.5 py-1 rounded-md bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-[10px] font-mono text-[#8B5CF6]">Fixed Lot</span>
                      <span className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] font-mono text-white/50">Adjustable per trade</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Max Drawdown Protection Card */}
              <div className="glass-strong rounded-xl p-5 border border-[#F59E0B]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-white mb-1">Max Drawdown Protection</h3>
                    <p className="font-body text-xs text-white/50 mb-3">Built-in safeguards to prevent account breach</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2.5 rounded-lg bg-white/3 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <Percent className="w-3 h-3 text-[#F59E0B]" />
                          <span className="font-mono text-sm font-bold text-[#F59E0B]">DD %</span>
                        </div>
                        <span className="font-body text-[10px] text-white/40">Set max DD in %</span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/3 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <DollarSign className="w-3 h-3 text-[#10B981]" />
                          <span className="font-mono text-sm font-bold text-[#10B981]">DD $</span>
                        </div>
                        <span className="font-body text-[10px] text-white/40">Set max DD in $</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clean Strategy Card */}
              <div className="glass-strong rounded-xl p-5 border border-[#10B981]/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-white mb-1">Clean Trading Strategy</h3>
                    <p className="font-body text-xs text-white/50 mb-3">Compliant with prop firm trading rules</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 text-[#10B981]" />
                        <span className="text-white/60">No Martingale or Grid strategies</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 text-[#10B981]" />
                        <span className="text-white/60">No high-frequency scalping</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 text-[#10B981]" />
                        <span className="text-white/60">Proper stop-loss on every trade</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Prop Firm Challenge Simulator */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-[#F59E0B]/15 via-[#10B981]/10 to-[#007AFF]/15 rounded-3xl blur-2xl opacity-60" />
              
              <div className="relative glass-strong rounded-2xl border border-white/10 overflow-hidden">
                {/* Card Header */}
                <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#F59E0B]/5 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                        <Trophy className="w-4 h-4 text-[#F59E0B]" />
                      </div>
                      <div>
                        <div className="font-heading font-semibold text-white text-sm">Challenge Compatibility</div>
                        <div className="font-body text-[10px] text-white/40">Standard prop firm rules</div>
                      </div>
                    </div>
                    <div className="px-2 py-1 rounded-full bg-[#10B981]/15 border border-[#10B981]/25">
                      <span className="font-mono text-[9px] text-[#10B981]">MT5 Ready</span>
                    </div>
                  </div>
                </div>

                {/* Challenge Rules Grid */}
                <div className="p-5 space-y-4">
                  {/* Profit Target */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#10B981]/5 to-transparent border border-[#10B981]/15">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-[#10B981]" />
                        <span className="font-body text-xs text-white/70">Profit Target</span>
                      </div>
                      <span className="font-mono text-xs text-[#10B981] font-semibold">8-10%</span>
                    </div>
                    <div className="font-body text-[10px] text-white/40">
                      EA's consistent returns help reach targets systematically
                    </div>
                  </div>

                  {/* Max Drawdown */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#F59E0B]/5 to-transparent border border-[#F59E0B]/15">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-[#F59E0B]" />
                        <span className="font-body text-xs text-white/70">Max Drawdown Limit</span>
                      </div>
                      <span className="font-mono text-xs text-[#F59E0B] font-semibold">10%</span>
                    </div>
                    <div className="font-body text-[10px] text-white/40">
                      Set EA's max DD in % or $ to stay within limits
                    </div>
                  </div>

                  {/* Daily Loss */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#EF4444]/5 to-transparent border border-[#EF4444]/15">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                        <span className="font-body text-xs text-white/70">Daily Loss Limit</span>
                      </div>
                      <span className="font-mono text-xs text-[#EF4444] font-semibold">5%</span>
                    </div>
                    <div className="font-body text-[10px] text-white/40">
                      Configure risk per trade to avoid daily limit breach
                    </div>
                  </div>

                  {/* Compatible Firms */}
                  <div className="pt-3 border-t border-white/5">
                    <div className="font-body text-[10px] text-white/40 mb-2">Works with firms that allow MT5 EAs:</div>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-mono text-white/50">FTMO</span>
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-mono text-white/50">Funded Next</span>
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-mono text-white/50">The5ers</span>
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-mono text-white/50">True Forex Funds</span>
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-mono text-white/50">+ More</span>
                    </div>
                  </div>

                  {/* CTA Note */}
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/20">
                    <Settings className="w-4 h-4 text-[#007AFF]" />
                    <span className="font-body text-[11px] text-[#007AFF]">
                      Fully configurable to match any prop firm's specific rules
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing - Single Plan - Attention Grabbing Design */}
      <section className="section-compact" id="pricing" data-testid="ea-pricing-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="relative"
          >
            {/* Outer Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-[#8B5CF6]/30 via-[#007AFF]/20 to-[#10B981]/30 rounded-3xl blur-2xl opacity-60" />
            
            {/* Main Card */}
            <div className="relative rounded-2xl overflow-hidden border border-[#8B5CF6]/30">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/10 via-[#0a0a12] to-[#10B981]/10" />
              
              {/* Animated Border Glow */}
              <div className="absolute inset-0 rounded-2xl" style={{
                background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)',
                animation: 'shimmer 3s infinite'
              }} />
              
              <div className="relative z-10 p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left - Pricing Info */}
                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#8B5CF6]/20 to-[#10B981]/20 border border-[#8B5CF6]/30 mb-4">
                      <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                      <span className="font-mono text-xs text-white/70">Limited Slots Available</span>
                    </div>

                    <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">
                      Get Monk Breakout EA
                    </h2>
                    <p className="font-body text-white/50 text-sm mb-6">
                      Join traders using our verified +228.66% return system
                    </p>
                    
                    {/* Price Display */}
                    <div className="flex items-baseline justify-center md:justify-start gap-2 mb-6">
                      <span className="font-heading font-bold text-5xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] via-[#007AFF] to-[#10B981]">
                        ₹10,000
                      </span>
                      <div className="flex flex-col items-start">
                        <span className="font-body text-white/40 text-sm">/month</span>
                        <span className="font-mono text-[10px] text-[#10B981]">Cancel anytime</span>
                      </div>
                    </div>

                    {/* Subscribe Button */}
                    <button 
                      onClick={handleSubscribe}
                      className="w-full md:w-auto bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] hover:from-[#9B6CF6] hover:to-[#1A8AFF] text-white font-heading font-semibold text-base px-8 py-4 rounded-xl flex items-center justify-center gap-2 group transition-all shadow-lg shadow-[#8B5CF6]/25 hover:shadow-[#8B5CF6]/40"
                      data-testid="ea-pricing-subscribe-btn"
                    >
                      <Zap size={18} className="group-hover:animate-pulse" />
                      Start Trading Now
                      <ExternalLink size={14} className="opacity-70" />
                    </button>
                    
                    <p className="font-body text-white/30 text-[10px] mt-3">
                      Secure payment via Razorpay
                    </p>
                  </div>

                  {/* Right - Features */}
                  <div className="space-y-3">
                    {/* Feature Cards */}
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                      <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-[#10B981]" />
                      </div>
                      <div>
                        <div className="font-heading font-medium text-white text-sm">2 MT5 Account Licenses</div>
                        <div className="font-body text-[10px] text-white/40">Use on personal + prop firm accounts</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                      <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div>
                        <div className="font-heading font-medium text-white text-sm">All Updates Included</div>
                        <div className="font-body text-[10px] text-white/40">Latest strategies & optimizations</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                      <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-[#8B5CF6]" />
                      </div>
                      <div>
                        <div className="font-heading font-medium text-white text-sm">Priority Support</div>
                        <div className="font-body text-[10px] text-white/40">Direct setup & troubleshooting help</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                      <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#F59E0B]" />
                      </div>
                      <div>
                        <div className="font-heading font-medium text-white text-sm">Monthly Performance Reports</div>
                        <div className="font-body text-[10px] text-white/40">Detailed backtest & live data</div>
                      </div>
                    </div>

                    {/* Stats Highlight */}
                    <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#007AFF]/10 border border-[#10B981]/20">
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <div className="font-mono font-bold text-lg text-[#10B981]">+228%</div>
                          <div className="font-body text-[9px] text-white/40">2025 Return</div>
                        </div>
                        <div>
                          <div className="font-mono font-bold text-lg text-[#007AFF]">78%</div>
                          <div className="font-body text-[9px] text-white/40">Win Rate</div>
                        </div>
                        <div>
                          <div className="font-mono font-bold text-lg text-[#8B5CF6]">2.47</div>
                          <div className="font-body text-[9px] text-white/40">Profit Factor</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-4" data-testid="ea-disclaimer-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="risk-disclaimer flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500/50 flex-shrink-0 mt-0.5" />
            <p className="font-body text-[10px] text-white/30 leading-relaxed">
              <strong className="text-white/40">Risk Disclaimer:</strong> Trading involves significant risk. Past backtest performance is not indicative of future results. Only trade with capital you can afford to lose. This is not financial advice.
            </p>
          </div>
        </div>
      </section>

      {/* Subscribe Modal */}
      <SubscribeModal 
        isOpen={showSubscribeModal} 
        onClose={() => setShowSubscribeModal(false)} 
      />
    </div>
  );
}
